// Utility to check completeness of each section in the ISET intake, based on isetFormConfig.js
// Usage: getSectionCompleteness(isetFormConfig, draftData) => [{ section, complete, missingFields }]

/**
 * Checks if a field is required, including conditional logic.
 * @param {object} question - The question config from isetFormConfig
 * @param {object} data - The current draft data
 * @returns {boolean}
 */
function isFieldRequired(question, data) {
  if (!question.required) return false;
  if (!question.conditional) return true;
  // Conditional required: check if the condition is met
  const { field, value } = question.conditional;
  if (Array.isArray(value)) {
    return value.includes(data[field]);
  }
  return data[field] === value;
}

/**
 * Checks if a field is filled in the draft data.
 * @param {object} question - The question config
 * @param {object} data - The current draft data
 * @returns {boolean}
 */
function isFieldFilled(question, data) {
  const val = data[question.id];
  if (val === undefined || val === null || val === "") return false;
  if (Array.isArray(val)) return val.length > 0;
  return true;
}

/**
 * Returns an array of section completeness objects.
 * @param {Array} formConfig - isetFormConfig.js export
 * @param {object} data - draft application data
 * @returns {Array<{section: string, complete: boolean, missingFields: string[], requiredCount: number, isMandatory: boolean}>}
 */
export function getSectionCompleteness(formConfig, data) {
  return formConfig.map(step => {
    const questions = step.questions || [];
    const requiredQuestions = questions.filter(q => isFieldRequired(q, data));
    const missingFields = requiredQuestions
      .filter(q => !isFieldFilled(q, data))
      .map(q => q.label || q.id);
    const hasAtLeastOneRequirement = Boolean(
      step.validation &&
      Array.isArray(step.validation.atLeastOne) &&
      step.validation.atLeastOne.length > 0
    );

    // Handle step-level group requirement: validation.atLeastOne (array of field ids)
    if (step.validation && Array.isArray(step.validation.atLeastOne)) {
      const atLeastOneFields = step.validation.atLeastOne;
      const oneProvided = atLeastOneFields.some(fid => {
        const v = data[fid];
        if (v === undefined || v === null) return false;
        if (Array.isArray(v)) return v.length > 0;
        if (typeof v === 'string') return v.trim() !== '';
        return v !== '';
      });
      if (!oneProvided) {
        // Use errorMessage if supplied, otherwise synthesize a label summary
        const groupMsg = step.validation.errorMessage || `Provide at least one: ${atLeastOneFields.join(', ')}`;
        missingFields.push(groupMsg);
      }
    }

    return {
      section: step.title,
      complete: missingFields.length === 0,
      missingFields,
      requiredCount: requiredQuestions.length,
      isMandatory: (requiredQuestions.length > 0) || hasAtLeastOneRequirement
    };
  });
}
